import { queryOptions, useQuery } from '@tanstack/react-query';
import { getInstruments, getPerpOrderLimits, listPerpOrderLimits, listPositionLimits } from '@/services/api/markets';

export const MARKETS_QUERY_KEY = ['markets'] as const;
export const INSTRUMENTS_QUERY_KEY = [...MARKETS_QUERY_KEY, 'instruments'] as const;
export const PERP_ORDER_LIMITS_QUERY_KEY = [...MARKETS_QUERY_KEY, 'perpOrderLimits'] as const;
export const POSITION_LIMITS_QUERY_KEY = [...MARKETS_QUERY_KEY, 'positionLimits'] as const;

export const instrumentsQueryOptions = () =>
  queryOptions({
    queryKey: INSTRUMENTS_QUERY_KEY,
    queryFn: getInstruments,
  });

/**
 * Full instruments list from the UNAUTHENTICATED FAST endpoint.
 * Deliberate fix: errors surface to the caller — legacy swallowed
 * them into an empty list indistinguishable from "no markets".
 */
export const useInstruments = () => useQuery(instrumentsQueryOptions());

/**
 * All futures markets' limits in one request — read-only table columns.
 * Under the MARKETS prefix with every other query here, so one invalidation
 * after a limits save refreshes them all.
 */
export const perpOrderLimitsListQueryOptions = () =>
  queryOptions({
    queryKey: PERP_ORDER_LIMITS_QUERY_KEY,
    queryFn: listPerpOrderLimits,
  });

export const usePerpOrderLimitsList = () => useQuery(perpOrderLimitsListQueryOptions());

export const perpOrderLimitsQueryOptions = (symbol: string) =>
  queryOptions({
    queryKey: [...PERP_ORDER_LIMITS_QUERY_KEY, symbol],
    // null = 404 = "no limits configured yet" — a successful result
    queryFn: () => getPerpOrderLimits(symbol),
    // Always fetch fresh on dialog open (legacy parity), and never deliver a late
    // background update that would reset a form mid-edit
    staleTime: 0,
    gcTime: 0,
  });

export const usePerpOrderLimits = (symbol?: string) => useQuery({ ...perpOrderLimitsQueryOptions(symbol ?? ''), enabled: Boolean(symbol) });

/** Every perp's max position ladder in one request — the table's per-segment columns and the edit dialog. */
export const positionLimitsListQueryOptions = () =>
  queryOptions({
    queryKey: POSITION_LIMITS_QUERY_KEY,
    queryFn: listPositionLimits,
  });

export const usePositionLimitsList = () => useQuery(positionLimitsListQueryOptions());
